/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  const rootPath = () => window.Shopify?.routes?.root || '/';

  class ProductInstructions extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.panels = [...this.querySelectorAll('[data-instruction-panel]')];
      this.index = Math.max(0, this.panels.findIndex((panel) => panel.classList.contains('is-active')));
      this.panels.forEach((panel, index) => {
        const active = index === this.index;
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });

      this.addEventListener('click', (event) => {
        const indexButton = event.target.closest('[data-instruction-index]');
        if (indexButton) return this.show(Number(indexButton.dataset.instructionIndex));
        if (event.target.closest('[data-instruction-prev]')) return this.show(this.index - 1);
        if (event.target.closest('[data-instruction-next]')) return this.show(this.index + 1);
      }, { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    show(nextIndex) {
      const count = this.panels.length;
      if (!count) return;
      this.index = (nextIndex + count) % count;

      this.panels.forEach((panel, index) => {
        const active = index === this.index;
        panel.classList.toggle('is-active', active);
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.querySelectorAll('[data-instruction-index]').forEach((button) => {
        const active = Number(button.dataset.instructionIndex) === this.index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'step' : 'false');
      });
    }
  }

  class ProductBundleForm extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.button = this.querySelector('[data-bundle-add]');
      this.error = this.querySelector('[data-bundle-error]');
      this.button?.addEventListener('click', () => this.addAll(), { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async addAll() {
      const items = [...this.querySelectorAll('[data-needed-variant-id]')].map((item) => ({
        id: Number(item.dataset.neededVariantId),
        quantity: 1
      }));
      if (!items.length || !this.button || this.button.disabled) return;

      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;

      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || 'The products could not be added.');

        const cartResponse = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        document.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('theme:toast', {
          detail: { message: 'Products added to your shopping bag.', type: 'success' }
        }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'The products could not be added.';
        if (this.error) {
          this.error.textContent = message;
          this.error.hidden = false;
        }
        document.dispatchEvent(new CustomEvent('theme:toast', { detail: { message, type: 'error' } }));
      } finally {
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('product-instructions')) customElements.define('product-instructions', ProductInstructions);
  if (!customElements.get('product-bundle-form')) customElements.define('product-bundle-form', ProductBundleForm);
})();
